import { Alignment, Direction, Spacing } from "@bonusx-global/bx-design-system";
import Stack from "@mui/material/Stack";
import { useMemo } from "react";
import { FeedbackEnum } from "../../../enums/feedback.enum";
import type { NegativeFeedbackReasonEnum } from "../../../enums/negative-feedback-reason.enum";
import {
  NEGATIVE_FEEDBACK_REQUEST_REASON_TITLE,
  NEGATIVE_FEEDBACK_THANK_YOU_TITLE,
  POSITIVE_FEEDBACK_TITLE,
} from "./ai-response-feedback.const";
import { AiResponseFeedbackButtons } from "./components/ai-response-feedback-buttons/ai-response-feedback-buttons";
import { AiResponseFeedbackReasonsChips } from "./components/ai-response-feedback-reasons-chips/ai-response-feedback-reasons-chips";
import AiResponseFeedbackTitle from "./components/ai-response-feedback-title/ai-response-feedback-title";
import { AiResponseFeedbackWrapper } from "./components/ai-response-feedback-wrapper/ai-response-feedback-wrapper";

interface AiResponseFeedbackProps {
  feedback: FeedbackEnum | null;
  negativeFeedbackReason: NegativeFeedbackReasonEnum | null;
  onFeedback?: (feedback: FeedbackEnum | null) => void;
  onNegativeFeedbackReason?: (reason: NegativeFeedbackReasonEnum) => void;
}

function AiResponseFeedback({
  feedback,
  negativeFeedbackReason,
  onFeedback,
  onNegativeFeedbackReason,
}: Readonly<AiResponseFeedbackProps>): JSX.Element {
  const isNegativeFeedback = feedback === FeedbackEnum.NEGATIVE;
  const isReasonMissing = negativeFeedbackReason === null;
  const shouldShowReasonsChips = isNegativeFeedback && isReasonMissing;

  function handleLike() {
    onFeedback?.(FeedbackEnum.POSITIVE);
  }

  function handleDislike() {
    onFeedback?.(FeedbackEnum.NEGATIVE);
  }

  function handleCloseClick() {
    onFeedback?.(null);
  }

  function handleReasonClick(reason: NegativeFeedbackReasonEnum) {
    onNegativeFeedbackReason?.(reason);
  }

  const isPositiveFeedback = feedback === FeedbackEnum.POSITIVE;
  const titleText = useMemo(() => {
    switch (true) {
      case isPositiveFeedback: {
        return POSITIVE_FEEDBACK_TITLE;
      }
      case isNegativeFeedback && isReasonMissing: {
        return NEGATIVE_FEEDBACK_REQUEST_REASON_TITLE;
      }
      case isNegativeFeedback && !isReasonMissing: {
        return NEGATIVE_FEEDBACK_THANK_YOU_TITLE;
      }
      default: {
        return "";
      }
    }
  }, [isReasonMissing, isNegativeFeedback, isPositiveFeedback]);

  const contentAlignment = isPositiveFeedback ? Alignment.flexEnd : Alignment.spaceBetween;

  return (
    <AiResponseFeedbackWrapper>
      <Stack
        direction={Direction.row}
        gap={Spacing[750]}
        justifyContent={contentAlignment}
        alignItems={Alignment.center}
      >
        <AiResponseFeedbackTitle
          text={titleText}
          showCloseButton={shouldShowReasonsChips}
          onCloseClick={handleCloseClick}
        />
        <AiResponseFeedbackButtons onLike={handleLike} onDislike={handleDislike} feedback={feedback} />
      </Stack>
      {shouldShowReasonsChips && <AiResponseFeedbackReasonsChips onReasonClick={handleReasonClick} />}
    </AiResponseFeedbackWrapper>
  );
}

export default AiResponseFeedback;
